import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { copyTextToClipboard, generateId } from "../../App.utils";
import { MadeWithLoveComponent } from "../../DesktopSidePanel";
import { GameShell } from "../../core/components/GameShell";
import { buildGamePath } from "../../core/games/gamePaths";
import { useCurrentPlayer } from "../../core/hooks/useCurrentPlayer";
import { useWindowSize } from "../../core/hooks/useWindowSize";
import {
  mergeRoomState,
  setRoomState,
  subscribeToRoomState,
} from "../../core/state/roomStore";
import {
  createTabooDeck,
  DEFAULT_TABOO_MODE,
  getTabooCardCount,
  TABOO_MODES,
} from "./words";

const DEFAULT_ROUND_DURATION = 60;
const DEFAULT_ACTIVE_TEAM = "red";
const MIN_ROUND_DURATION = 15;
const MAX_ROUND_DURATION = 120;
const ROUND_DURATION_STEP = 15;
const TURN_OUTCOMES = {
  CORRECT: "correct",
  FAULT: "fault",
  SKIP: "skip",
};
const GAME_STATUS = {
  LOBBY: "lobby",
  PLAYING: "playing",
  FINISHED: "finished",
};
const TEAM_ORDER = ["red", "blue"];
const TEAM_LABELS = {
  red: "Red",
  blue: "Blue",
};
const COPY_FEEDBACK_MS = 1200;

function normalizeTurnCounts(turnCounts = {}) {
  return {
    red: turnCounts.red || 0,
    blue: turnCounts.blue || 0,
  };
}

function getTeamPlayers(players, team, activeOnly = true) {
  return Object.values(players || {})
    .filter((player) => {
      if (activeOnly && !player?.active) {
        return false;
      }

      return player?.team === team;
    })
    .sort((firstPlayer, secondPlayer) => {
      const firstJoinedAt = firstPlayer?.joinedAt || "";
      const secondJoinedAt = secondPlayer?.joinedAt || "";

      if (firstJoinedAt !== secondJoinedAt) {
        return firstJoinedAt.localeCompare(secondJoinedAt);
      }

      return (firstPlayer?.name || "").localeCompare(secondPlayer?.name || "");
    });
}

function getSuggestedTeam(players) {
  const redCount = getTeamPlayers(players, "red", false).length;
  const blueCount = getTeamPlayers(players, "blue", false).length;

  return redCount <= blueCount ? "red" : "blue";
}

function resolveActiveTurn(
  players,
  preferredTeam = DEFAULT_ACTIVE_TEAM,
  turnCounts = {},
) {
  const normalizedCounts = normalizeTurnCounts(turnCounts);
  const bothTeamsReady = TEAM_ORDER.every(
    (team) => getTeamPlayers(players, team, false).length > 0,
  );

  if (!bothTeamsReady) {
    return {
      activeTeam: preferredTeam,
      activePlayerId: null,
    };
  }

  const preferredPlayers = getTeamPlayers(players, preferredTeam, false);

  if (preferredPlayers.length > 0) {
    return {
      activeTeam: preferredTeam,
      activePlayerId:
        preferredPlayers[
          normalizedCounts[preferredTeam] % preferredPlayers.length
        ].playerId,
    };
  }

  const fallbackTeam = TEAM_ORDER.find(
    (team) => getTeamPlayers(players, team, false).length > 0,
  );

  if (!fallbackTeam) {
    return {
      activeTeam: DEFAULT_ACTIVE_TEAM,
      activePlayerId: null,
    };
  }

  const fallbackPlayers = getTeamPlayers(players, fallbackTeam, false);

  return {
    activeTeam: fallbackTeam,
    activePlayerId:
      fallbackPlayers[
        normalizedCounts[fallbackTeam] % fallbackPlayers.length
      ].playerId,
  };
}

function getNextTurn(players, activeTeam, turnCounts) {
  const normalizedCounts = normalizeTurnCounts(turnCounts);
  const completedTeam = activeTeam || DEFAULT_ACTIVE_TEAM;
  const updatedTurnCounts = {
    ...normalizedCounts,
    [completedTeam]: normalizedCounts[completedTeam] + 1,
  };
  const opposingTeam = completedTeam === "red" ? "blue" : "red";
  const nextPreferredTeam =
    getTeamPlayers(players, opposingTeam, false).length > 0
      ? opposingTeam
      : completedTeam;
  const resolvedTurn = resolveActiveTurn(
    players,
    nextPreferredTeam,
    updatedTurnCounts,
  );

  return {
    ...resolvedTurn,
    turnCounts: updatedTurnCounts,
  };
}

function buildRoomState(modeKey, roundDuration, players = {}) {
  const now = new Date().toISOString();
  const safeRoundDuration = clampRoundDuration(roundDuration);
  const turnCounts = normalizeTurnCounts();
  const resolvedTurn = resolveActiveTurn(
    players,
    DEFAULT_ACTIVE_TEAM,
    turnCounts,
  );

  return {
    gameSlug: "taboo",
    status: GAME_STATUS.LOBBY,
    modeKey,
    deck: createTabooDeck(modeKey),
    completedCount: 0,
    redScore: 0,
    blueScore: 0,
    roundDuration: safeRoundDuration,
    roundStartedAt: null,
    roundActive: false,
    pausedAt: null,
    totalPausedTime: 0,
    turnEntries: [],
    turnReviewActive: false,
    turnReviewReason: null,
    turnCounts,
    activeTeam: resolvedTurn.activeTeam,
    activePlayerId: null,
    gameMessage: "Select at least one player on each team to start.",
    createdAt: now,
    updatedAt: now,
    players,
  };
}

function createRoomSnapshot(
  roomPlayers = {},
  modeKey = DEFAULT_TABOO_MODE,
  roundDuration = DEFAULT_ROUND_DURATION,
) {
  const newGameId = generateId();
  const gameData = buildRoomState(modeKey, roundDuration, roomPlayers);

  return {
    newGameId,
    gameData,
  };
}

function formatTime(seconds) {
  const safeSeconds = Math.max(0, seconds);
  const minutes = Math.floor(safeSeconds / 60);
  const remainder = safeSeconds % 60;
  return `${minutes}:${remainder.toString().padStart(2, "0")}`;
}

function normalizePlayerName(name) {
  return name.trim().replace(/\s+/g, " ").slice(0, 24);
}

function clampRoundDuration(duration) {
  return Math.max(
    MIN_ROUND_DURATION,
    Math.min(MAX_ROUND_DURATION, duration),
  );
}

function areWordListsEqual(firstList = [], secondList = []) {
  if (firstList === secondList) {
    return true;
  }

  if (firstList.length !== secondList.length) {
    return false;
  }

  return firstList.every((word, index) => word === secondList[index]);
}

function areDecksEqual(firstDeck = [], secondDeck = []) {
  if (firstDeck === secondDeck) {
    return true;
  }

  if (firstDeck.length !== secondDeck.length) {
    return false;
  }

  return firstDeck.every((card, index) => {
    const comparisonCard = secondDeck[index];

    return (
      card?.id === comparisonCard?.id &&
      card?.word === comparisonCard?.word &&
      areWordListsEqual(card?.taboo || [], comparisonCard?.taboo || [])
    );
  });
}

function areTurnEntriesEqual(firstEntries = [], secondEntries = []) {
  if (firstEntries === secondEntries) {
    return true;
  }

  if (firstEntries.length !== secondEntries.length) {
    return false;
  }

  return firstEntries.every((entry, index) => {
    const comparisonEntry = secondEntries[index];

    return (
      entry?.id === comparisonEntry?.id &&
      entry?.cardId === comparisonEntry?.cardId &&
      entry?.word === comparisonEntry?.word &&
      entry?.outcome === comparisonEntry?.outcome &&
      areWordListsEqual(entry?.taboo || [], comparisonEntry?.taboo || [])
    );
  });
}

function arePlayerRecordsEqual(firstPlayer, secondPlayer) {
  if (firstPlayer === secondPlayer) {
    return true;
  }

  return (
    firstPlayer?.playerId === secondPlayer?.playerId &&
    firstPlayer?.name === secondPlayer?.name &&
    firstPlayer?.active === secondPlayer?.active &&
    firstPlayer?.isSpymaster === secondPlayer?.isSpymaster &&
    firstPlayer?.team === secondPlayer?.team &&
    firstPlayer?.joinedAt === secondPlayer?.joinedAt
  );
}

function arePlayersEqual(firstPlayers = {}, secondPlayers = {}) {
  if (firstPlayers === secondPlayers) {
    return true;
  }

  const firstIds = Object.keys(firstPlayers);
  const secondIds = Object.keys(secondPlayers);

  if (firstIds.length !== secondIds.length) {
    return false;
  }

  return firstIds.every((playerId) =>
    arePlayerRecordsEqual(firstPlayers[playerId], secondPlayers[playerId]),
  );
}

function areTurnCountsEqual(firstTurnCounts = {}, secondTurnCounts = {}) {
  const firstCounts = normalizeTurnCounts(firstTurnCounts);
  const secondCounts = normalizeTurnCounts(secondTurnCounts);

  return (
    firstCounts.red === secondCounts.red &&
    firstCounts.blue === secondCounts.blue
  );
}

function syncCurrentPlayer(players, currentPlayer, joinedAt) {
  if (!currentPlayer) {
    return players;
  }

  return {
    ...players,
    [currentPlayer.playerId]: {
      ...players[currentPlayer.playerId],
      ...currentPlayer,
      team: players[currentPlayer.playerId]?.team || getSuggestedTeam(players),
      joinedAt: players[currentPlayer.playerId]?.joinedAt || joinedAt,
    },
  };
}

function buildTurnEntry(card, outcome) {
  return {
    id: generateId(),
    cardId: card.id,
    word: card.word,
    taboo: [...card.taboo],
    outcome,
  };
}

function applyTurnOutcome(deck, cardId, outcome) {
  const cardIndex = deck.findIndex((card) => card.id === cardId);

  if (cardIndex === -1) {
    return {
      applied: false,
      deck,
    };
  }

  const nextDeck = [...deck];
  const [targetCard] = nextDeck.splice(cardIndex, 1);

  if (outcome !== TURN_OUTCOMES.CORRECT) {
    nextDeck.push(targetCard);
  }

  return {
    applied: true,
    deck: nextDeck,
  };
}

function applyTurnEntries(deck, turnEntries = []) {
  return turnEntries.reduce(
    (workingDeck, entry) =>
      applyTurnOutcome(workingDeck, entry.cardId, entry.outcome).deck,
    deck,
  );
}

function summarizeTurnEntries(turnEntries = []) {
  return turnEntries.reduce(
    (summary, entry) => {
      if (entry.outcome === TURN_OUTCOMES.CORRECT) {
        summary.correct += 1;
      } else if (entry.outcome === TURN_OUTCOMES.FAULT) {
        summary.fault += 1;
      } else {
        summary.skip += 1;
      }

      return summary;
    },
    { correct: 0, fault: 0, skip: 0 },
  );
}

function finalizeTurnEntries({
  deck,
  turnEntries,
  activeTeam,
  completedCount,
  redScore,
  blueScore,
}) {
  let nextDeck = [...deck];
  let nextCompletedCount = completedCount;
  let nextRedScore = redScore;
  let nextBlueScore = blueScore;

  turnEntries.forEach((entry) => {
    const result = applyTurnOutcome(nextDeck, entry.cardId, entry.outcome);

    if (!result.applied) {
      return;
    }

    nextDeck = result.deck;

    if (entry.outcome === TURN_OUTCOMES.CORRECT) {
      nextCompletedCount += 1;
      if (activeTeam === "red") {
        nextRedScore += 1;
      } else {
        nextBlueScore += 1;
      }
      return;
    }

    if (entry.outcome === TURN_OUTCOMES.FAULT) {
      if (activeTeam === "red") {
        nextRedScore -= 1;
      } else {
        nextBlueScore -= 1;
      }
    }
  });

  return {
    deck: nextDeck,
    completedCount: nextCompletedCount,
    redScore: nextRedScore,
    blueScore: nextBlueScore,
  };
}

function deriveGameStatus(data = {}) {
  if (data.status) {
    return data.status;
  }

  if (Array.isArray(data.deck) && data.deck.length === 0) {
    return GAME_STATUS.FINISHED;
  }

  if (
    data.roundStartedAt ||
    data.roundActive ||
    data.completedCount > 0 ||
    data.redScore > 0 ||
    data.blueScore > 0
  ) {
    return GAME_STATUS.PLAYING;
  }

  return GAME_STATUS.LOBBY;
}

function DeckHeaderPicker({
  modeKey,
  onChange,
  disabled,
  onShare,
  shareJustCopied,
  accentTeam,
}) {
  return (
    <div className={`taboo-deck-header-picker ${accentTeam || "blue"}`}>
      <button
        aria-label={shareJustCopied ? "Link copied" : "Copy room link"}
        className={`copy-link taboo-room-copy-link ${shareJustCopied ? "copied" : ""}`}
        onClick={onShare}
        type="button"
      >
        {shareJustCopied ? <CheckIcon /> : <CopyIcon />}
      </button>
      <label className="taboo-deck-select-wrap">
        <select
          aria-label="Deck"
          className="taboo-deck-select"
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          value={modeKey}
        >
          {TABOO_MODES.map((mode) => (
            <option key={mode.key} value={mode.key}>
              {mode.label}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

function CopyIcon() {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        height="11"
        rx="2"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
        width="11"
        x="9"
        y="9"
      />
      <path
        d="M7 15H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v1"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="m5 12 4 4 10-10"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2.4"
      />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M4 20h4l10.5-10.5a2.12 2.12 0 0 0-3-3L5 17v3z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
      <path
        d="M13.5 6l4.5 4.5"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
    </svg>
  );
}

function DurationPicker({ roundDuration, onChange, disabled }) {
  const canDecrease = roundDuration > MIN_ROUND_DURATION;
  const canIncrease = roundDuration < MAX_ROUND_DURATION;

  return (
    <div className="taboo-duration-picker">
      <button
        className="new-game-btn taboo-stepper-btn"
        disabled={disabled || !canDecrease}
        onClick={() => onChange(roundDuration - ROUND_DURATION_STEP)}
        type="button"
      >
        -15
      </button>
      <div className="taboo-duration-value">
        <span className="taboo-duration-seconds">{roundDuration}</span>
        <span className="taboo-duration-unit">seconds</span>
      </div>
      <button
        className="new-game-btn taboo-stepper-btn"
        disabled={disabled || !canIncrease}
        onClick={() => onChange(roundDuration + ROUND_DURATION_STEP)}
        type="button"
      >
        +15
      </button>
    </div>
  );
}

function TeamScoreRing({ team, score }) {
  return (
    <div className={`taboo-score-ring ${team}`}>
      <span>{score}</span>
    </div>
  );
}

function TeamScoreBlock({ team, score, activeTeam, roundActive, subtitle }) {
  const statusCopy =
    activeTeam === team ? (roundActive ? "Playing" : "Up Next") : subtitle;

  return (
    <div className={`taboo-team-score-block taboo-team-score-block-${team}`}>
      <div className="taboo-team-score-copy">
        <p className="taboo-team-score-label">{TEAM_LABELS[team]}</p>
        <p className="taboo-team-score-subtitle">
          {statusCopy}
        </p>
      </div>
      <p className={`taboo-team-score-value ${team}`}>{score}</p>
    </div>
  );
}

const TeamRoster = React.memo(function TeamRoster({
  team,
  players,
  activePlayerId,
  currentPlayerId,
  currentTeam = null,
  lobbyEditable = false,
  isEditingName = false,
  nameDraft = "",
  canSaveName = false,
  onNameDraftChange,
  onNameEditStart,
  onNameSave,
  onNameCancel,
  onSelectTeam,
}) {
  const isCurrentTeam = currentTeam === team;
  const canSelectTeam = lobbyEditable && onSelectTeam;

  const handleTeamSelect = () => {
    if (!canSelectTeam) {
      return;
    }

    onSelectTeam(team);
  };

  return (
    <div
      className={`taboo-prompt-card taboo-team-roster taboo-team-card ${team} taboo-team-roster-${team} ${canSelectTeam ? "interactive" : ""} ${isCurrentTeam ? "current" : ""}`}
      onClick={canSelectTeam ? handleTeamSelect : undefined}
      onKeyDown={
        canSelectTeam
          ? (event) => {
              if (event.target !== event.currentTarget) {
                return;
              }

              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleTeamSelect();
              }
            }
          : undefined
      }
      role={canSelectTeam ? "button" : undefined}
      tabIndex={canSelectTeam ? 0 : undefined}
    >
      <div className="taboo-prompt-card-header taboo-team-roster-header">
        <div className="taboo-team-roster-copy">
          <h3 className="taboo-prompt-word taboo-team-card-title">{TEAM_LABELS[team]} Team</h3>
        </div>
      </div>
      <div className="taboo-prompt-card-body taboo-team-roster-list">
        {players.length > 0 && (
          players.map((player) => {
            const isCurrentPlayer = player.playerId === currentPlayerId;
            const isActivePlayer = player.playerId === activePlayerId;
            const rowClassName = `taboo-prompt-line taboo-player-row ${isActivePlayer ? "active" : ""} ${isCurrentPlayer ? "current" : ""}`;

            if (lobbyEditable && isCurrentPlayer) {
              if (isEditingName) {
                return (
                  <div
                    key={player.playerId}
                    className={`${rowClassName} taboo-player-editor`}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <input
                      autoFocus
                      className="taboo-name-input"
                      maxLength={24}
                      onChange={(event) => onNameDraftChange(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          onNameSave();
                        }

                        if (event.key === "Escape") {
                          onNameCancel();
                        }
                      }}
                      placeholder="Your name"
                      type="text"
                      value={nameDraft}
                    />
                    <div className="taboo-player-editor-actions">
                      <button
                        aria-label="Save name"
                        className="new-game-btn taboo-player-editor-btn taboo-player-editor-btn-save"
                        disabled={!canSaveName}
                        onClick={(event) => {
                          event.stopPropagation();
                          onNameSave();
                        }}
                        title="Save"
                        type="button"
                      >
                        &#10003;
                      </button>
                      <button
                        aria-label="Cancel editing"
                        className="new-game-btn taboo-player-editor-btn taboo-player-editor-btn-cancel"
                        onClick={(event) => {
                          event.stopPropagation();
                          onNameCancel();
                        }}
                        title="Cancel"
                        type="button"
                      >
                        &times;
                      </button>
                    </div>
                  </div>
                );
              }

              return (
                <button
                  key={player.playerId}
                  className={`${rowClassName} taboo-player-row-button`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onNameEditStart();
                  }}
                  type="button"
                >
                  <span className="taboo-player-name">
                    {player.name}
                    {" (You)"}
                  </span>
                  <span className="taboo-player-row-actions">
                    <span className="taboo-player-row-action" aria-hidden="true">
                      <EditIcon />
                    </span>
                  </span>
                </button>
              );
            }

            return (
              <div key={player.playerId} className={rowClassName}>
                <span className="taboo-player-name">
                  {player.name}
                  {isCurrentPlayer ? " (You)" : ""}
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
});

const TabooPromptCard = React.memo(function TabooPromptCard({
  word = "---",
  tabooWords = [],
  compact = false,
  review = false,
  team = "blue",
}) {
  const sizeClassName = compact
    ? "taboo-prompt-card-compact"
    : review
      ? "taboo-prompt-card-review"
      : "";
  const cardClassName = ["taboo-prompt-card", team, sizeClassName]
    .filter(Boolean)
    .join(" ");
  const headerClassName = `taboo-prompt-card-header ${
    review ? "taboo-prompt-card-header-review" : "taboo-prompt-card-header-main"
  }`;

  return (
    <article className={cardClassName}>
      <div className={headerClassName}>
        <h3 className="taboo-prompt-word">{word}</h3>
      </div>
      <div className="taboo-prompt-card-body">
        {tabooWords.map((tabooWord) => (
          <p key={`${word}-${tabooWord}`} className="taboo-prompt-line">
            {tabooWord}
          </p>
        ))}
      </div>
    </article>
  );
});

const TurnReviewRow = React.memo(function TurnReviewRow({
  entry,
  team = "blue",
  disabled,
  onChange,
}) {
  return (
    <div className="taboo-review-row">
      <div className="taboo-review-row-header">
        <div className="taboo-review-copy">
          <TabooPromptCard
            review
            team={team}
            tabooWords={entry.taboo}
            word={entry.word}
          />
        </div>

        <div className="toggle-buttons taboo-review-toggle">
          {[
            {
              label: "Skip",
              outcome: TURN_OUTCOMES.SKIP,
            },
            {
              label: "Fault",
              outcome: TURN_OUTCOMES.FAULT,
            },
            {
              label: "Correct",
              outcome: TURN_OUTCOMES.CORRECT,
            },
          ].map((option) => (
            <button
              key={`${entry.id}-${option.outcome}`}
              className={`toggle-btn taboo-review-toggle-btn taboo-review-toggle-btn-${option.outcome} ${entry.outcome === option.outcome ? "active" : ""}`}
              disabled={disabled}
              onClick={() => onChange(entry.id, option.outcome)}
              type="button"
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
});

function PlayerNameStat({ player = null, currentPlayerId = null, fallback = "Waiting" }) {
  const isCurrentPlayer =
    player?.playerId && currentPlayerId && player.playerId === currentPlayerId;
  const playerClassName = player?.team ? `taboo-player-name ${player.team}` : "taboo-player-name";
  const isInactive = player?.active === false;

  return (
    <span className={playerClassName}>
      {isInactive ? "💀 " : ""}
      {player?.name || fallback}
      {isCurrentPlayer ? (
        <span className={`taboo-player-self ${player?.team || ""}`}> (you)</span>
      ) : null}
    </span>
  );
}

const LiveSidebar = React.memo(function LiveSidebar({
  compact = false,
  activeTeam,
  roundActive,
  turnReviewActive,
  turnSummary,
  turnEntriesCount,
  pausedAt,
  redScore,
  blueScore,
  activePlayer,
  nextPlayer,
  currentPlayerId,
  canControlRound,
  canEndInactiveTurn = false,
  onRoundToggle,
  onEndTurn,
  onNewGame,
  onShare,
  shareJustCopied = false,
  timerText,
  progress,
  displayCompletedCount,
  totalCards,
  gameMessage,
}) {
  const wrapperClassName = compact
    ? "side-panel-mobile taboo-live-sidebar"
    : "side-panel taboo-live-sidebar";
  const roundButtonLabel = turnReviewActive
    ? "Reviewing Turn"
    : roundActive
    ? pausedAt
      ? "Resume Round"
      : "Pause Round"
    : "Start Round";
  const roundLocked = !activePlayer?.name || !canControlRound || turnReviewActive;
  const showEndTurn =
    roundActive && (canControlRound || canEndInactiveTurn) && !turnReviewActive;

  return (
    <div className={wrapperClassName}>
      <div className="game-info taboo-game-info taboo-live-info">
        <div className="taboo-sidebar-turn-row">
          <TeamScoreRing team="red" score={redScore} />
          <h2 className={`taboo-sidebar-turn-title ${activeTeam}`}>
            {TEAM_LABELS[activeTeam]}'s Turn
            <button
              aria-label={shareJustCopied ? "Link copied" : "Copy game link"}
              className={`taboo-sidebar-copy ${shareJustCopied ? "copied" : ""}`}
              onClick={onShare}
              type="button"
            >
              {shareJustCopied ? <CheckIcon /> : <CopyIcon />}
            </button>
          </h2>
          <TeamScoreRing team="blue" score={blueScore} />
        </div>

        {!turnReviewActive && (
          <div className="timer-box taboo-timer-box">
            <span className="timer-display">{timerText}</span>
          </div>
        )}
        {gameMessage && (
          <div className="message-content taboo-message">{"🐧 "}{gameMessage}</div>
        )}
        {canControlRound && !turnReviewActive && (
          <>
            <button
              className={`end-turn-button ${activeTeam}`}
              disabled={roundLocked}
              onClick={onRoundToggle}
              type="button"
            >
              {roundButtonLabel}
            </button>

          </>
        )}

        {showEndTurn && (
          <button
            className="new-game-btn taboo-end-turn"
            onClick={onEndTurn}
            type="button"
          >
            End Turn
          </button>
        )}

        <button
          className="new-game-btn taboo-sidebar-new-game"
          onClick={onNewGame}
          type="button"
        >
          New Game
        </button>

        <div className="taboo-mobile-stats taboo-sidebar-stats">
          <div className="taboo-mobile-stat">
            <p className="taboo-card-label">Current</p>
            <p className="taboo-mobile-stat-value">
              <PlayerNameStat
                currentPlayerId={currentPlayerId}
                fallback="Waiting"
                player={activePlayer}
              />
            </p>
          </div>
          <div className="taboo-mobile-stat">
            <p className="taboo-card-label">Next</p>
            <p className="taboo-mobile-stat-value">
              <PlayerNameStat
                currentPlayerId={currentPlayerId}
                fallback="Waiting"
                player={nextPlayer}
              />
            </p>
          </div>
        </div>

        {turnReviewActive && compact && (
          <div className="taboo-mobile-stats taboo-sidebar-stats">
            <div className="taboo-mobile-stat">
              <p className="taboo-card-label">Correct</p>
              <p className="taboo-mobile-stat-value">{turnSummary.correct}</p>
            </div>
            <div className="taboo-mobile-stat">
              <p className="taboo-card-label">Faults</p>
              <p className="taboo-mobile-stat-value">{turnSummary.fault}</p>
            </div>
            <div className="taboo-mobile-stat">
              <p className="taboo-card-label">Skipped</p>
              <p className="taboo-mobile-stat-value">{turnSummary.skip}</p>
            </div>
            <div className="taboo-mobile-stat">
              <p className="taboo-card-label">Cards Seen</p>
              <p className="taboo-mobile-stat-value">{turnEntriesCount}</p>
            </div>
          </div>
        )}

        <div className="taboo-sidebar-progress">
          <div className="taboo-progress-copy">
            <span>Progress</span>
            <span>
              {displayCompletedCount} / {totalCards}
            </span>
          </div>
          <div className="taboo-progress-track">
            <div
              className={`taboo-progress-bar ${activeTeam}`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

      </div>
    </div>
  );
});

const LiveCardStage = React.memo(function LiveCardStage({
  canInteractWithCard,
  compact = false,
  currentCard,
  effectiveActiveTeam,
  onTurnOutcome,
}) {
  const actionRowClassName = compact
    ? "taboo-action-row taboo-action-row-compact taboo-live-action-row taboo-live-action-row-compact"
    : "taboo-action-row taboo-live-action-row";
  const stageClassName = compact
    ? "taboo-live-stage taboo-live-stage-compact"
    : "taboo-live-stage";

  return (
    <div className={stageClassName}>
      <div className={`taboo-card-shell taboo-live-card-shell ${effectiveActiveTeam}`}>
        <div className="taboo-card-content">
          <TabooPromptCard
            compact={compact}
            team={effectiveActiveTeam}
            tabooWords={currentCard?.taboo || []}
            word={currentCard?.word || "---"}
          />
        </div>
      </div>

      <div className={actionRowClassName}>
        <button
          className="taboo-action-btn taboo-action-btn-skip"
          disabled={!canInteractWithCard}
          onClick={() => onTurnOutcome(TURN_OUTCOMES.SKIP)}
          type="button"
        >
          Pass
        </button>
        <button
          className="taboo-action-btn taboo-action-btn-fault"
          disabled={!canInteractWithCard}
          onClick={() => onTurnOutcome(TURN_OUTCOMES.FAULT)}
          type="button"
        >
          Fault
        </button>
        <button
          className="taboo-action-btn taboo-action-btn-correct"
          disabled={!canInteractWithCard}
          onClick={() => onTurnOutcome(TURN_OUTCOMES.CORRECT)}
          type="button"
        >
          Correct
        </button>
      </div>
    </div>
  );
});

const ReviewStage = React.memo(function ReviewStage({
  cardStatusCopy,
  canReviewTurn,
  reviewTeam,
  turnEntries,
  turnSummary,
  onFinalize,
  onTurnEntryOutcomeChange,
}) {
  return (
    <div className="taboo-live-stage taboo-review-stage">
      <div className={`taboo-card-shell taboo-review-shell ${reviewTeam}`}>
        <div className="taboo-card-top">
          <div>
            <p className="taboo-card-label">Round Review</p>
            <p className="taboo-card-helper">{cardStatusCopy}</p>
          </div>
        </div>

        <div className="taboo-review-summary">
          <div className="taboo-mobile-stat">
            <p className="taboo-card-label">Correct</p>
            <p className="taboo-mobile-stat-value">{turnSummary.correct}</p>
          </div>
          <div className="taboo-mobile-stat">
            <p className="taboo-card-label">Faults</p>
            <p className="taboo-mobile-stat-value">{turnSummary.fault}</p>
          </div>
          <div className="taboo-mobile-stat">
            <p className="taboo-card-label">Skipped</p>
            <p className="taboo-mobile-stat-value">{turnSummary.skip}</p>
          </div>
          <div className="taboo-mobile-stat">
            <p className="taboo-card-label">Cards Seen</p>
            <p className="taboo-mobile-stat-value">{turnEntries.length}</p>
          </div>
        </div>

        {turnEntries.length > 0 ? (
          <div className="taboo-review-list">
            {turnEntries.map((entry) => (
              <TurnReviewRow
                key={entry.id}
                disabled={!canReviewTurn}
                entry={entry}
                onChange={onTurnEntryOutcomeChange}
                team={reviewTeam}
              />
            ))}
          </div>
        ) : (
          <p className="taboo-review-empty">No cards logged.</p>
        )}
      </div>

      <div className="taboo-action-row taboo-review-actions">
        <button
          className="taboo-action-btn taboo-action-btn-correct"
          disabled={!canReviewTurn}
          onClick={onFinalize}
          type="button"
        >
          Finalize Turn
        </button>
      </div>
    </div>
  );
});

function Taboo({ gameId, gameDefinition }) {
  const navigate = useNavigate();
  const { width } = useWindowSize();
  const isCreatingGameRef = useRef(false);
  const isFinishingTurnRef = useRef(false);
  const joinTimestampRef = useRef(new Date().toISOString());
  const pendingRoomRef = useRef(null);
  const copyFeedbackTimeoutRef = useRef(null);

  const [modeKey, setModeKey] = useState(DEFAULT_TABOO_MODE);
  const [gameStatus, setGameStatus] = useState(GAME_STATUS.LOBBY);
  const [deck, setDeck] = useState(() => createTabooDeck(DEFAULT_TABOO_MODE));
  const [completedCount, setCompletedCount] = useState(0);
  const [redScore, setRedScore] = useState(0);
  const [blueScore, setBlueScore] = useState(0);
  const [roundDuration, setRoundDuration] = useState(DEFAULT_ROUND_DURATION);
  const [roundStartedAt, setRoundStartedAt] = useState(null);
  const [roundActive, setRoundActive] = useState(false);
  const [pausedAt, setPausedAt] = useState(null);
  const [totalPausedTime, setTotalPausedTime] = useState(0);
  const [turnEntries, setTurnEntries] = useState([]);
  const [turnReviewActive, setTurnReviewActive] = useState(false);
  const [turnReviewReason, setTurnReviewReason] = useState(null);
  const [timer, setTimer] = useState(DEFAULT_ROUND_DURATION);
  const [turnCounts, setTurnCounts] = useState(normalizeTurnCounts());
  const [activeTeam, setActiveTeam] = useState(DEFAULT_ACTIVE_TEAM);
  const [activePlayerId, setActivePlayerId] = useState(null);
  const [gameMessage, setGameMessage] = useState(
    "Select at least one player on each team to start.",
  );
  const [players, setPlayers] = useState({});
  const [currentPlayer, setCurrentPlayer] = useCurrentPlayer();
  const [nameDraft, setNameDraft] = useState("");
  const [isEditingName, setIsEditingName] = useState(false);
  const [shareJustCopied, setShareJustCopied] = useState(false);

  useEffect(() => {
    return () => {
      if (copyFeedbackTimeoutRef.current) {
        clearTimeout(copyFeedbackTimeoutRef.current);
      }
    };
  }, []);

  const applyRoomState = useCallback((data) => {
    setGameStatus(deriveGameStatus(data));
    if ("modeKey" in data && data.modeKey) {
      setModeKey(data.modeKey);
    }
    if (Array.isArray(data.deck)) {
      setDeck((previousDeck) =>
        areDecksEqual(previousDeck, data.deck) ? previousDeck : data.deck,
      );
    }
    if ("completedCount" in data) {
      setCompletedCount(data.completedCount);
    }
    if ("redScore" in data) {
      setRedScore(data.redScore);
    }
    if ("blueScore" in data) {
      setBlueScore(data.blueScore);
    }
    if ("roundDuration" in data) {
      setRoundDuration(clampRoundDuration(data.roundDuration));
    }
    if ("roundStartedAt" in data) {
      setRoundStartedAt(data.roundStartedAt);
    }
    if ("roundActive" in data) {
      setRoundActive(data.roundActive);
    }
    if ("pausedAt" in data) {
      setPausedAt(data.pausedAt);
    }
    if ("totalPausedTime" in data) {
      setTotalPausedTime(data.totalPausedTime);
    }
    if ("turnEntries" in data) {
      const nextTurnEntries = Array.isArray(data.turnEntries)
        ? data.turnEntries
        : [];
      setTurnEntries((previousTurnEntries) =>
        areTurnEntriesEqual(previousTurnEntries, nextTurnEntries)
          ? previousTurnEntries
          : nextTurnEntries,
      );
    }
    if ("turnReviewActive" in data) {
      setTurnReviewActive(Boolean(data.turnReviewActive));
    }
    if ("turnReviewReason" in data) {
      setTurnReviewReason(data.turnReviewReason || null);
    }
    if ("turnCounts" in data) {
      const nextTurnCounts = normalizeTurnCounts(data.turnCounts);
      setTurnCounts((previousTurnCounts) =>
        areTurnCountsEqual(previousTurnCounts, nextTurnCounts)
          ? previousTurnCounts
          : nextTurnCounts,
      );
    }
    if ("activeTeam" in data && data.activeTeam) {
      setActiveTeam(data.activeTeam);
    }
    if ("activePlayerId" in data) {
      setActivePlayerId(data.activePlayerId);
    }
    if ("gameMessage" in data) {
      setGameMessage(data.gameMessage);
    }
    if ("players" in data && data.players) {
      setPlayers((previousPlayers) =>
        arePlayersEqual(previousPlayers, data.players)
          ? previousPlayers
          : data.players,
      );
    }
  }, []);

  useEffect(() => {
    setNameDraft(currentPlayer?.name || "");
  }, [currentPlayer?.name]);

  const updateGameState = useCallback(
    async (updates) => {
      if (!gameId) {
        return;
      }

      try {
        await mergeRoomState(gameId, updates);
      } catch (error) {
        console.error("Error updating taboo state:", error);
        if (error.code === "resource-exhausted") {
          setGameMessage("Too many updates. Give it a beat.");
        }
      }
    },
    [gameId],
  );

  const rosterPlayers = useMemo(
    () =>
      syncCurrentPlayer(players, currentPlayer, joinTimestampRef.current),
    [currentPlayer, players],
  );
  const currentRoomPlayer = currentPlayer
    ? rosterPlayers[currentPlayer.playerId] || null
    : null;

  useEffect(() => {
    joinTimestampRef.current = new Date().toISOString();
  }, [gameId]);

  if (!gameId && !pendingRoomRef.current) {
    const joinedAt = joinTimestampRef.current;
    const initialPlayers = currentPlayer
      ? {
          [currentPlayer.playerId]: {
            ...currentPlayer,
            team: getSuggestedTeam({}),
            joinedAt,
          },
        }
      : {};

    pendingRoomRef.current = createRoomSnapshot(
      initialPlayers,
      DEFAULT_TABOO_MODE,
      DEFAULT_ROUND_DURATION,
    );
  }

  useEffect(() => {
    if (gameId) {
      pendingRoomRef.current = null;
      isCreatingGameRef.current = false;
      return;
    }

    if (isCreatingGameRef.current || !pendingRoomRef.current) {
      return;
    }

    let isMounted = true;
    isCreatingGameRef.current = true;
    const { newGameId, gameData } = pendingRoomRef.current;

    applyRoomState(gameData);
    setTimer(gameData.roundDuration);

    setRoomState(newGameId, gameData)
      .catch((error) => {
        console.error("Error creating taboo room:", error);
        if (!isMounted) {
          return;
        }
        setGameMessage("Couldn't create the room. Try refreshing.");
      })
      .finally(() => {
        if (isMounted) {
          isCreatingGameRef.current = false;
        }
      });

    return () => {
      isMounted = false;
    };
  }, [applyRoomState, gameId]);

  useEffect(() => {
    if (!gameId || !currentPlayer) {
      return;
    }

    const existingRoomPlayer = players[currentPlayer.playerId];
    const nextRoomPlayer = rosterPlayers[currentPlayer.playerId];

    if (arePlayerRecordsEqual(existingRoomPlayer, nextRoomPlayer)) {
      return;
    }

    updateGameState({
      players: {
        ...players,
        [currentPlayer.playerId]: nextRoomPlayer,
      },
    });
  }, [currentPlayer, gameId, players, rosterPlayers, updateGameState]);

  useEffect(() => {
    if (!gameId) {
      return undefined;
    }

    const unsubscribe = subscribeToRoomState(gameId, (data) => {
      if (!data) {
        return;
      }

      if (data.redirectToGame) {
        navigate(buildGamePath(gameDefinition.slug, data.redirectToGame));
        return;
      }

      applyRoomState(data);
    });

    return unsubscribe;
  }, [applyRoomState, gameDefinition.slug, gameId, navigate]);

  const calculateRemainingTime = useCallback(
    (referenceTime = new Date()) => {
      if (!roundStartedAt) {
        return roundDuration;
      }

      const effectiveTime = pausedAt ? new Date(pausedAt) : referenceTime;
      const elapsedSeconds =
        Math.floor((effectiveTime - new Date(roundStartedAt)) / 1000) -
        totalPausedTime;

      return Math.max(0, roundDuration - elapsedSeconds);
    },
    [pausedAt, roundDuration, roundStartedAt, totalPausedTime],
  );

  useEffect(() => {
    if (!roundStartedAt) {
      setTimer(roundDuration);
      return;
    }

    setTimer(calculateRemainingTime());
  }, [calculateRemainingTime, roundDuration, roundStartedAt]);

  const resolvedCurrentTurn = resolveActiveTurn(
    rosterPlayers,
    activeTeam,
    turnCounts,
  );
  const effectiveActivePlayerId =
    gameStatus === GAME_STATUS.LOBBY
      ? resolvedCurrentTurn.activePlayerId
      : activePlayerId;
  const activePlayer = effectiveActivePlayerId
    ? rosterPlayers[effectiveActivePlayerId] || null
    : null;
  const effectiveActiveTeam =
    gameStatus === GAME_STATUS.LOBBY
      ? resolvedCurrentTurn.activeTeam
      : activePlayer?.team || activeTeam;
  const liveDeck = applyTurnEntries(deck, turnEntries);
  const currentCard = liveDeck[0] || null;
  const turnSummary = summarizeTurnEntries(turnEntries);
  const totalCards = getTabooCardCount(modeKey);
  const isLobby = gameStatus === GAME_STATUS.LOBBY;
  const gameFinished =
    gameStatus === GAME_STATUS.FINISHED || deck.length === 0;
  const isPlaying = gameStatus === GAME_STATUS.PLAYING && !gameFinished;
  const isActivePlayer =
    currentPlayer?.playerId &&
    currentPlayer.playerId === effectiveActivePlayerId;
  const activePlayerInactive = activePlayer?.active === false;
  const canControlRound = isPlaying && isActivePlayer && effectiveActivePlayerId;
  const canEndInactiveTurn =
    isPlaying &&
    roundActive &&
    activePlayerInactive &&
    !isActivePlayer &&
    currentPlayer?.playerId &&
    !turnReviewActive;
  const displayCompletedCount =
    isActivePlayer && (roundActive || turnReviewActive)
      ? completedCount + turnSummary.correct
      : completedCount;
  const progress = totalCards > 0 ? (displayCompletedCount / totalCards) * 100 : 0;
  const redPlayers = getTeamPlayers(rosterPlayers, "red");
  const bluePlayers = getTeamPlayers(rosterPlayers, "blue");
  const teamsReady = redPlayers.length > 0 && bluePlayers.length > 0;
  const nextTurn = getNextTurn(rosterPlayers, effectiveActiveTeam, turnCounts);
  const nextPlayer = nextTurn.activePlayerId
    ? rosterPlayers[nextTurn.activePlayerId] || null
    : null;
  const showLiveCardStage =
    isPlaying &&
    isActivePlayer &&
    effectiveActivePlayerId &&
    roundActive &&
    !pausedAt &&
    !turnReviewActive;
  const isDesktop = width >= 768;
  const canInteractWithCard =
    canControlRound &&
    roundActive &&
    !pausedAt;
  const canReviewTurn = canControlRound && turnReviewActive;
  const showReviewStage =
    isPlaying && isActivePlayer && effectiveActivePlayerId && turnReviewActive;
  const showActiveStage = showLiveCardStage || showReviewStage;
  const needsTurnRecovery =
    isPlaying &&
    !turnReviewActive &&
    !roundActive &&
    !effectiveActivePlayerId;
  const canManageTeams = currentPlayer && (isLobby || needsTurnRecovery);
  const currentTeam = currentRoomPlayer?.team || getSuggestedTeam(rosterPlayers);
  const winningTeam =
    redScore === blueScore ? null : redScore > blueScore ? "red" : "blue";
  const turnMetaCopy = formatTime(timer);

  useEffect(() => {
    if (!roundActive) {
      isFinishingTurnRef.current = false;
    }
  }, [roundActive]);

  useEffect(() => {
    if (!isPlaying || !roundActive || effectiveActivePlayerId || gameFinished) {
      return;
    }

    const message = `Waiting for someone to join the ${TEAM_LABELS[activeTeam]} team.`;
    const now = new Date().toISOString();

    setRoundActive(false);
    setRoundStartedAt(null);
    setPausedAt(null);
    setTotalPausedTime(0);
    setTurnEntries([]);
    setTurnReviewActive(false);
    setTurnReviewReason(null);
    setTimer(roundDuration);
    setGameMessage(message);
    updateGameState({
      roundActive: false,
      roundStartedAt: null,
      pausedAt: null,
      totalPausedTime: 0,
      turnEntries: [],
      turnReviewActive: false,
      turnReviewReason: null,
      gameMessage: message,
      updatedAt: now,
    });
  }, [
    activeTeam,
    effectiveActivePlayerId,
    gameFinished,
    isPlaying,
    roundActive,
    roundDuration,
    updateGameState,
  ]);

  const beginTurnReview = useCallback(
    (baseMessage, reviewEntries = turnEntries) => {
      if (
        !isPlaying ||
        gameFinished ||
        !isActivePlayer ||
        !effectiveActivePlayerId
      ) {
        return;
      }

      if (isFinishingTurnRef.current) {
        return;
      }

      isFinishingTurnRef.current = true;

      const reviewMessage =
        reviewEntries.length > 0
          ? `${baseMessage} Review the round before locking it in.`
          : `${baseMessage} No cards were logged. Confirm to pass it on.`;
      const now = new Date().toISOString();

      setRoundActive(false);
      setRoundStartedAt(null);
      setPausedAt(null);
      setTotalPausedTime(0);
      setTimer(0);
      setTurnEntries(reviewEntries);
      setTurnReviewActive(true);
      setTurnReviewReason(baseMessage);
      setGameMessage(reviewMessage);
      updateGameState({
        roundActive: false,
        roundStartedAt: null,
        pausedAt: null,
        totalPausedTime: 0,
        turnEntries: reviewEntries,
        turnReviewActive: true,
        turnReviewReason: baseMessage,
        gameMessage: reviewMessage,
        updatedAt: now,
      });
    },
    [
      effectiveActivePlayerId,
      gameFinished,
      isActivePlayer,
      isPlaying,
      turnEntries,
      updateGameState,
    ],
  );

  useEffect(() => {
    if (!isPlaying || !roundActive || pausedAt || timer <= 0 || gameFinished) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      setTimer((previousTimer) => {
        if (previousTimer <= 1) {
          if (isActivePlayer) {
            window.setTimeout(() => {
              beginTurnReview("Time is up.");
            }, 0);
          }
          return 0;
        }

        return previousTimer - 1;
      });
    }, 1000);

    return () => window.clearInterval(interval);
  }, [
    beginTurnReview,
    gameFinished,
    isActivePlayer,
    isPlaying,
    pausedAt,
    roundActive,
    timer,
  ]);

  const handleRoundToggle = useCallback(() => {
    if (!canControlRound || turnReviewActive) {
      return;
    }

    if (!roundActive) {
      const now = new Date().toISOString();
      const message = `${activePlayer?.name || "Player"} is live for ${TEAM_LABELS[effectiveActiveTeam]}.`;

      isFinishingTurnRef.current = false;
      setActiveTeam(effectiveActiveTeam);
      setActivePlayerId(effectiveActivePlayerId);
      setRoundActive(true);
      setRoundStartedAt(now);
      setPausedAt(null);
      setTotalPausedTime(0);
      setTurnEntries([]);
      setTurnReviewActive(false);
      setTurnReviewReason(null);
      setTimer(roundDuration);
      setGameMessage(message);
      updateGameState({
        activeTeam: effectiveActiveTeam,
        activePlayerId: effectiveActivePlayerId,
        roundDuration,
        roundActive: true,
        roundStartedAt: now,
        pausedAt: null,
        totalPausedTime: 0,
        turnEntries: [],
        turnReviewActive: false,
        turnReviewReason: null,
        gameMessage: message,
        updatedAt: now,
      });
      return;
    }

    const now = new Date();

    if (!pausedAt) {
      const pausedIso = now.toISOString();

      setPausedAt(pausedIso);
      setTimer(calculateRemainingTime(now));
      updateGameState({
        pausedAt: pausedIso,
        updatedAt: pausedIso,
      });
      return;
    }

    const additionalPauseTime = Math.floor((now - new Date(pausedAt)) / 1000);
    const nextPausedTime = totalPausedTime + additionalPauseTime;

    setPausedAt(null);
    setTotalPausedTime(nextPausedTime);
    updateGameState({
      pausedAt: null,
      totalPausedTime: nextPausedTime,
      updatedAt: now.toISOString(),
    });
  }, [
    activePlayer?.name,
    calculateRemainingTime,
    canControlRound,
    effectiveActivePlayerId,
    effectiveActiveTeam,
    pausedAt,
    roundActive,
    roundDuration,
    totalPausedTime,
    turnReviewActive,
    updateGameState,
  ]);

  const logTurnOutcome = useCallback((outcome) => {
    if (!canInteractWithCard || !currentCard) {
      return;
    }

    const nextTurnEntries = [...turnEntries, buildTurnEntry(currentCard, outcome)];
    const nextDeckPreview = applyTurnEntries(deck, nextTurnEntries);
    const now = new Date().toISOString();

    setTurnEntries(nextTurnEntries);
    updateGameState({
      turnEntries: nextTurnEntries,
      updatedAt: now,
    });

    if (nextDeckPreview.length === 0) {
      window.setTimeout(() => {
        beginTurnReview("Deck cleared.", nextTurnEntries);
      }, 0);
    }
  }, [
    beginTurnReview,
    canInteractWithCard,
    currentCard,
    deck,
    turnEntries,
    updateGameState,
  ]);

  const handleTurnEntryOutcomeChange = (entryId, nextOutcome) => {
    if (!canReviewTurn) {
      return;
    }

    const nextTurnEntries = turnEntries.map((entry) =>
      entry.id === entryId
        ? {
            ...entry,
            outcome: nextOutcome,
          }
        : entry,
    );

    setTurnEntries(nextTurnEntries);
    updateGameState({
      turnEntries: nextTurnEntries,
      updatedAt: new Date().toISOString(),
    });
  };

  const handleFinalizeTurnReview = () => {
    if (!canReviewTurn) {
      return;
    }

    const finalizedTurn = finalizeTurnEntries({
      deck,
      turnEntries,
      activeTeam: effectiveActiveTeam,
      completedCount,
      redScore,
      blueScore,
    });
    const now = new Date().toISOString();

    if (finalizedTurn.deck.length === 0) {
      const finishedMessage =
        finalizedTurn.redScore === finalizedTurn.blueScore
          ? `Deck cleared. Tied at ${finalizedTurn.redScore}-${finalizedTurn.blueScore}.`
          : `${finalizedTurn.redScore > finalizedTurn.blueScore ? TEAM_LABELS.red : TEAM_LABELS.blue} team wins ${Math.max(finalizedTurn.redScore, finalizedTurn.blueScore)}-${Math.min(finalizedTurn.redScore, finalizedTurn.blueScore)}.`;

      setGameStatus(GAME_STATUS.FINISHED);
      setDeck(finalizedTurn.deck);
      setCompletedCount(finalizedTurn.completedCount);
      setRedScore(finalizedTurn.redScore);
      setBlueScore(finalizedTurn.blueScore);
      setRoundActive(false);
      setRoundStartedAt(null);
      setPausedAt(null);
      setTotalPausedTime(0);
      setTurnEntries([]);
      setTurnReviewActive(false);
      setTurnReviewReason(null);
      setActivePlayerId(null);
      setTimer(0);
      setGameMessage(finishedMessage);
      updateGameState({
        status: GAME_STATUS.FINISHED,
        deck: finalizedTurn.deck,
        completedCount: finalizedTurn.completedCount,
        redScore: finalizedTurn.redScore,
        blueScore: finalizedTurn.blueScore,
        roundActive: false,
        roundStartedAt: null,
        pausedAt: null,
        totalPausedTime: 0,
        turnEntries: [],
        turnReviewActive: false,
        turnReviewReason: null,
        activePlayerId: null,
        gameMessage: finishedMessage,
        updatedAt: now,
      });
      return;
    }

    const nextResolvedTurn = getNextTurn(
      rosterPlayers,
      effectiveActiveTeam,
      turnCounts,
    );
    const nextPlayerName =
      nextResolvedTurn.activePlayerId &&
      rosterPlayers[nextResolvedTurn.activePlayerId]?.name;
    const nextTurnLead =
      turnReviewReason === "Time is up." || turnReviewReason === "Turn ended."
        ? turnReviewReason
        : "Turn locked.";
    const nextTurnMessage = nextPlayerName
      ? `${nextTurnLead} ${nextPlayerName} is up for ${TEAM_LABELS[nextResolvedTurn.activeTeam]}.`
      : `${nextTurnLead} Waiting for someone to join the ${TEAM_LABELS[nextResolvedTurn.activeTeam]} team.`;

    setDeck(finalizedTurn.deck);
    setCompletedCount(finalizedTurn.completedCount);
    setRedScore(finalizedTurn.redScore);
    setBlueScore(finalizedTurn.blueScore);
    setRoundActive(false);
    setRoundStartedAt(null);
    setPausedAt(null);
    setTotalPausedTime(0);
    setTimer(roundDuration);
    setActiveTeam(nextResolvedTurn.activeTeam);
    setActivePlayerId(nextResolvedTurn.activePlayerId);
    setTurnEntries([]);
    setTurnReviewActive(false);
    setTurnReviewReason(null);
    setTurnCounts(nextResolvedTurn.turnCounts);
    setGameMessage(nextTurnMessage);
    updateGameState({
      deck: finalizedTurn.deck,
      completedCount: finalizedTurn.completedCount,
      redScore: finalizedTurn.redScore,
      blueScore: finalizedTurn.blueScore,
      roundActive: false,
      roundStartedAt: null,
      pausedAt: null,
      totalPausedTime: 0,
      activeTeam: nextResolvedTurn.activeTeam,
      activePlayerId: nextResolvedTurn.activePlayerId,
      turnEntries: [],
      turnReviewActive: false,
      turnReviewReason: null,
      turnCounts: nextResolvedTurn.turnCounts,
      gameMessage: nextTurnMessage,
      updatedAt: now,
    });
  };

  const handleEndTurn = useCallback(() => {
    if ((!canControlRound && !canEndInactiveTurn) || !roundActive) {
      return;
    }

    beginTurnReview("Turn ended.");
  }, [beginTurnReview, canControlRound, canEndInactiveTurn, roundActive]);

  const resetRoom = (selectedModeKey) => {
    const nextPlayers = currentPlayer
      ? {
          ...players,
          [currentPlayer.playerId]: {
            ...players[currentPlayer.playerId],
            ...currentPlayer,
            team:
              players[currentPlayer.playerId]?.team || getSuggestedTeam(players),
            joinedAt:
              players[currentPlayer.playerId]?.joinedAt ||
              joinTimestampRef.current,
          },
        }
      : players;
    const nextState = {
      ...buildRoomState(selectedModeKey, roundDuration, nextPlayers),
      updatedAt: new Date().toISOString(),
    };

    applyRoomState(nextState);
    setTimer(roundDuration);
    updateGameState(nextState);
  };

  const handleNameSave = () => {
    if (!currentPlayer || (isPlaying && !needsTurnRecovery)) {
      return;
    }

    const nextName = normalizePlayerName(nameDraft || "");

    if (!nextName || nextName === currentPlayer.name) {
      setNameDraft(currentPlayer.name || "");
      setIsEditingName(false);
      return;
    }

    const nextPlayer = {
      ...players[currentPlayer.playerId],
      ...currentPlayer,
      name: nextName,
      team: players[currentPlayer.playerId]?.team || getSuggestedTeam(players),
      joinedAt:
        players[currentPlayer.playerId]?.joinedAt || joinTimestampRef.current,
    };

    setCurrentPlayer((previousPlayer) =>
      previousPlayer
        ? {
            ...previousPlayer,
            name: nextName,
          }
        : previousPlayer,
    );
    setPlayers((previousPlayers) => ({
      ...previousPlayers,
      [currentPlayer.playerId]: nextPlayer,
    }));
    setIsEditingName(false);
    setGameMessage(`You're now ${nextName}.`);
    updateGameState({
      players: {
        ...players,
        [currentPlayer.playerId]: nextPlayer,
      },
      gameMessage: `You're now ${nextName}.`,
      updatedAt: new Date().toISOString(),
    });
  };

  const handleNameEditStart = () => {
    if (!currentPlayer || (isPlaying && !needsTurnRecovery)) {
      return;
    }

    setNameDraft(currentPlayer.name || "");
    setIsEditingName(true);
  };

  const handleNameEditCancel = () => {
    setNameDraft(currentPlayer?.name || "");
    setIsEditingName(false);
  };

  const handleModeChange = (nextModeKey) => {
    if (isPlaying || nextModeKey === modeKey) {
      return;
    }

    resetRoom(nextModeKey);
  };

  const handleTeamChange = (nextTeam) => {
    if (
      !currentPlayer ||
      (isPlaying && !needsTurnRecovery) ||
      nextTeam === currentTeam
    ) {
      return;
    }

    const nextPlayer = {
      ...players[currentPlayer.playerId],
      ...currentPlayer,
      team: nextTeam,
      joinedAt:
        players[currentPlayer.playerId]?.joinedAt || joinTimestampRef.current,
    };

    setPlayers((previousPlayers) => ({
      ...previousPlayers,
      [currentPlayer.playerId]: nextPlayer,
    }));
    setNameDraft(nextPlayer.name || "");
    setIsEditingName(false);
    setGameMessage(`Joined ${TEAM_LABELS[nextTeam]} team.`);
    updateGameState({
      players: {
        ...players,
        [currentPlayer.playerId]: nextPlayer,
      },
      gameMessage: `Joined ${TEAM_LABELS[nextTeam]} team.`,
      updatedAt: new Date().toISOString(),
    });
  };

  const handleStartGame = () => {
    if (isPlaying && !needsTurnRecovery) {
      return;
    }

    if (!teamsReady) {
      setGameMessage("You need at least one player on each team.");
      return;
    }

    const resolvedTurn = resolveActiveTurn(
      rosterPlayers,
      needsTurnRecovery ? activeTeam : DEFAULT_ACTIVE_TEAM,
      turnCounts,
    );

    if (!resolvedTurn.activePlayerId) {
      setGameMessage("Waiting for a player.");
      return;
    }

    const message = `${rosterPlayers[resolvedTurn.activePlayerId]?.name || "Player"} starts for ${TEAM_LABELS[resolvedTurn.activeTeam]}.`;
    const now = new Date().toISOString();

    setGameStatus(GAME_STATUS.PLAYING);
    setActiveTeam(resolvedTurn.activeTeam);
    setActivePlayerId(resolvedTurn.activePlayerId);
    setTurnEntries([]);
    setTurnReviewActive(false);
    setTurnReviewReason(null);
    setGameMessage(message);
    updateGameState({
      players: rosterPlayers,
      status: GAME_STATUS.PLAYING,
      activeTeam: resolvedTurn.activeTeam,
      activePlayerId: resolvedTurn.activePlayerId,
      roundDuration,
      turnEntries: [],
      turnReviewActive: false,
      turnReviewReason: null,
      gameMessage: message,
      updatedAt: now,
    });
  };

  const createFreshRoom = useCallback(
    async ({ redirectCurrentRoom = false } = {}) => {
    const joinedAt = new Date().toISOString();
    joinTimestampRef.current = joinedAt;
    const refreshedPlayers = gameId
      ? rosterPlayers
      : currentPlayer
        ? {
            [currentPlayer.playerId]: {
              ...currentPlayer,
              team: getSuggestedTeam({}),
              joinedAt,
            },
          }
        : {};
    const { newGameId, gameData } = createRoomSnapshot(
      refreshedPlayers,
      modeKey,
      roundDuration,
    );

      try {
        await setRoomState(newGameId, gameData);

        if (gameId && redirectCurrentRoom) {
          await mergeRoomState(gameId, {
            redirectToGame: newGameId,
            gameMessage: "Starting new game...",
            updatedAt: new Date().toISOString(),
          });
        }

        applyRoomState(gameData);
        setTimer(gameData.roundDuration);
        navigate(buildGamePath(gameDefinition.slug, newGameId), {
          replace: !gameId,
        });
        return newGameId;
      } catch (error) {
        console.error("Error creating new taboo room:", error);
        setGameMessage("Could not spin up a new room. Try again.");
        throw error;
      }
    },
    [
      applyRoomState,
      currentPlayer,
      gameDefinition.slug,
      gameId,
      modeKey,
      navigate,
      rosterPlayers,
      roundDuration,
    ],
  );

  const handlePlayAgain = async () => {
    if (
      gameId &&
      isPlaying &&
      !window.confirm(
        "Start a new game for everyone? The current round will end.",
      )
    ) {
      return;
    }

    setGameMessage("Creating new game...");

    try {
      await createFreshRoom({ redirectCurrentRoom: Boolean(gameId) });
    } catch (error) {
      // createFreshRoom already sets the room error state
    }
  };

  const handleShare = async () => {
    let shareUrl = window.location.href;

    if (!gameId) {
      const newGameId = await createFreshRoom();
      shareUrl = `${window.location.origin}${buildGamePath(
        gameDefinition.slug,
        newGameId,
      )}`;
    }

    const copied = await copyTextToClipboard(shareUrl);
    if (copyFeedbackTimeoutRef.current) {
      clearTimeout(copyFeedbackTimeoutRef.current);
    }
    setShareJustCopied(copied);
    if (copied) {
      copyFeedbackTimeoutRef.current = window.setTimeout(() => {
        setShareJustCopied(false);
        copyFeedbackTimeoutRef.current = null;
      }, COPY_FEEDBACK_MS);
    }
    setGameMessage(
      copied ? "Room link copied." : "Couldn't copy the room link on this browser.",
    );
  };

  const handleRoundDurationChange = (nextDuration) => {
    if (isPlaying) {
      return;
    }

    const normalizedDuration = clampRoundDuration(nextDuration);
    setRoundDuration(normalizedDuration);
    setTimer(normalizedDuration);

    if (gameId) {
      updateGameState({
        roundDuration: normalizedDuration,
        updatedAt: new Date().toISOString(),
      });
    }
  };

  const normalizedNameDraft = normalizePlayerName(nameDraft || "");
  const canSaveName =
    currentPlayer &&
    normalizedNameDraft &&
    normalizedNameDraft !== (currentPlayer?.name || "");
  const lobbyMessage = teamsReady
    ? needsTurnRecovery
      ? `${activePlayer?.name || "A player"} is ready for ${TEAM_LABELS[effectiveActiveTeam]} when you resume.`
      : `${activePlayer?.name || "A player"} starts for ${TEAM_LABELS[activeTeam]}.`
    : needsTurnRecovery
      ? "Each team needs one active player before the game can resume."
      : "Select at least one player on each team to start.";

  const startButtonLabel = needsTurnRecovery ? "Resume Game" : "Start Game";
  const startButtonDisabled = !teamsReady;
  const winnerTitle = winningTeam
    ? `${TEAM_LABELS[winningTeam]} Team Wins`
    : "Tie Game";
  const cardStatusCopy = !activePlayerId
    ? "Waiting for both teams."
    : gameFinished
      ? "Deck cleared. Reset the room or start another one."
      : turnReviewActive
        ? isActivePlayer
          ? "Review every card from this round before you lock the score."
          : `${activePlayer?.name || "The active player"} is reviewing the round.`
      : isActivePlayer
        ? roundActive
          ? pausedAt
            ? "Round paused. Resume when everyone is ready."
            : "Describe it without using the banned words."
          : "Press Start Turn when you're ready and the card will appear."
        : roundActive
          ? `${activePlayer?.name || "The active player"} can see the live card right now.`
          : `${activePlayer?.name || "The next player"} is getting ready.`;
  const lobbyBoard = (
    <div className="taboo-room">
      <div className="taboo-room-grid taboo-room-grid-lobby">
        <section className="taboo-room-panel taboo-room-panel-wide">
          <div className="taboo-room-section">
            <div className="taboo-rosters">
              <TeamRoster
                team="red"
                players={redPlayers}
                activePlayerId={activePlayerId}
                currentPlayerId={currentPlayer?.playerId}
                currentTeam={currentTeam}
                lobbyEditable={canManageTeams}
                isEditingName={isEditingName && currentTeam === "red"}
                nameDraft={nameDraft}
                canSaveName={canSaveName}
                onNameDraftChange={setNameDraft}
                onNameEditStart={handleNameEditStart}
                onNameSave={handleNameSave}
                onNameCancel={handleNameEditCancel}
                onSelectTeam={handleTeamChange}
              />
              <TeamRoster
                team="blue"
                players={bluePlayers}
                activePlayerId={activePlayerId}
                currentPlayerId={currentPlayer?.playerId}
                currentTeam={currentTeam}
                lobbyEditable={canManageTeams}
                isEditingName={isEditingName && currentTeam === "blue"}
                nameDraft={nameDraft}
                canSaveName={canSaveName}
                onNameDraftChange={setNameDraft}
                onNameEditStart={handleNameEditStart}
                onNameSave={handleNameSave}
                onNameCancel={handleNameEditCancel}
                onSelectTeam={handleTeamChange}
              />
            </div>
          </div>
        </section>

        <div className="taboo-room-side-stack">
          <section className="taboo-room-panel taboo-room-setup-panel">
            <div className="taboo-room-panel-toolbar">
              <DeckHeaderPicker
                accentTeam={currentTeam}
                modeKey={modeKey}
                onChange={handleModeChange}
                disabled={!isLobby}
                onShare={handleShare}
                shareJustCopied={shareJustCopied}
              />
            </div>

            <div className="taboo-room-section">
              <p className="taboo-sidebar-label">Round Size</p>
              <DurationPicker
                roundDuration={roundDuration}
                onChange={handleRoundDurationChange}
                disabled={!isLobby}
              />
            </div>

            <div className="taboo-room-actions taboo-room-actions-lobby">
              <button
                className={`end-turn-button ${currentTeam}`}
                disabled={startButtonDisabled}
                onClick={handleStartGame}
                title={startButtonDisabled ? "Select one player on each team first." : undefined}
                type="button"
              >
                {startButtonLabel}
              </button>
            </div>

            <div className="message-content taboo-message">
              {"🐧 "}
              {lobbyMessage}
            </div>
          </section>

          <MadeWithLoveComponent />
        </div>
      </div>
    </div>
  );

  const finishedBoard = (
    <div className="taboo-room">
      <div className={`taboo-room-hero ${winningTeam || "neutral"}`}>
        <p className="taboo-overline">{gameDefinition.name}</p>
        <h1 className="taboo-room-title">{winnerTitle}</h1>
      </div>

      <div className="taboo-room-grid">
        <section className="taboo-room-panel">
          <div className="taboo-team-scores">
            <TeamScoreBlock
              team="red"
              score={redScore}
              activeTeam={null}
              roundActive={false}
              subtitle="Final"
            />
            <TeamScoreBlock
              team="blue"
              score={blueScore}
              activeTeam={null}
              roundActive={false}
              subtitle="Final"
            />
          </div>

          <div className="taboo-room-actions">
            <button
              className={`end-turn-button ${winningTeam || activeTeam}`}
              onClick={handlePlayAgain}
              type="button"
            >
              New Game
            </button>
          </div>

        </section>

        <section className="taboo-room-panel taboo-room-panel-wide">
          <div className="taboo-room-section">
            <div className="taboo-rosters">
              <TeamRoster
                team="red"
                players={redPlayers}
                activePlayerId={null}
                currentPlayerId={currentPlayer?.playerId}
              />
              <TeamRoster
                team="blue"
                players={bluePlayers}
                activePlayerId={null}
                currentPlayerId={currentPlayer?.playerId}
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  );

  const showRecoveryBoard = isLobby || needsTurnRecovery;

  const sharedSidebarProps = {
    activeTeam: effectiveActiveTeam,
    roundActive,
    turnReviewActive,
    turnSummary,
    turnEntriesCount: turnEntries.length,
    pausedAt,
    redScore,
    blueScore,
    activePlayer,
    nextPlayer,
    currentPlayerId: currentPlayer?.playerId,
    canControlRound,
    canEndInactiveTurn,
    onRoundToggle: handleRoundToggle,
    onEndTurn: handleEndTurn,
    onNewGame: handlePlayAgain,
    onShare: handleShare,
    shareJustCopied,
    timerText: turnMetaCopy,
    progress,
    displayCompletedCount,
    totalCards,
    gameMessage,
  };

  const desktopScorePanel = <LiveSidebar {...sharedSidebarProps} />;
  const mobileScorePanel = <LiveSidebar compact {...sharedSidebarProps} />;
  const activeStage = showReviewStage ? (
    <ReviewStage
      canReviewTurn={canReviewTurn}
      cardStatusCopy={cardStatusCopy}
      onFinalize={handleFinalizeTurnReview}
      onTurnEntryOutcomeChange={handleTurnEntryOutcomeChange}
      reviewTeam={effectiveActiveTeam}
      turnEntries={turnEntries}
      turnSummary={turnSummary}
    />
  ) : showLiveCardStage ? (
    <LiveCardStage
      canInteractWithCard={canInteractWithCard}
      compact={!isDesktop}
      currentCard={currentCard}
      effectiveActiveTeam={effectiveActiveTeam}
      onTurnOutcome={logTurnOutcome}
    />
  ) : null;
  const activeStageBoard = showActiveStage ? (
    isDesktop ? (
      <div className="taboo-board taboo-board-live">
        <div className="taboo-board-card">{activeStage}</div>
      </div>
    ) : (
      <div className="taboo-mobile-live">
        {activeStage}
        {mobileScorePanel}
      </div>
    )
  ) : null;
  const scorePanelBoard = isDesktop ? desktopScorePanel : mobileScorePanel;
  const showScorePanelOnly =
    isPlaying && !gameFinished && !showActiveStage;
  const useDesktopSplitShell = isDesktop && showActiveStage;
  const showDesktopBelowShellFooter =
    isDesktop && (showActiveStage || showScorePanelOnly);

  const displayedBoard = showRecoveryBoard
    ? lobbyBoard
    : gameFinished
      ? finishedBoard
      : showActiveStage
        ? activeStageBoard
        : showScorePanelOnly
          ? scorePanelBoard
          : scorePanelBoard;

  if (!gameId && pendingRoomRef.current) {
    return (
      <Navigate
        replace
        to={buildGamePath(gameDefinition.slug, pendingRoomRef.current.newGameId)}
      />
    );
  }

  return (
    <GameShell
      appClassName="App-taboo"
      width={width}
      board={displayedBoard}
      desktopSidebar={useDesktopSplitShell ? desktopScorePanel : null}
      footer={
        showActiveStage || showDesktopBelowShellFooter || showRecoveryBoard
          ? null
          : <MadeWithLoveComponent />
      }
      belowShell={showDesktopBelowShellFooter ? <MadeWithLoveComponent /> : null}
      shellClassName={
        useDesktopSplitShell
          ? "game-board-taboo-live"
          : ""
      }
    />
  );
}

export default Taboo;
